import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Send, ShieldCheck, Calendar, Crown, AlertTriangle, Clock, X, MapPin, CheckCircle, XCircle, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import TimeToSayGoodbye from "@/components/TimeToSayGoodbye";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chat = () => {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Meetup status controls
  const [activeMeetup, setActiveMeetup] = useState(null);
  const [showMeetupControls, setShowMeetupControls] = useState(false);
  const [runningLateMinutes, setRunningLateMinutes] = useState(15);
  const [showGoodbyeModal, setShowGoodbyeModal] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [messagesRes, convRes] = await Promise.all([
          axios.get(`${API}/conversations/${conversationId}/messages`),
          axios.get(`${API}/conversations`)
        ]);
        
        setMessages(messagesRes.data.messages || []);
        
        const conv = convRes.data.conversations?.find(c => c.conversation_id === conversationId);
        if (conv) {
          setOtherUser(conv.other_user);
        }
      } catch (error) {
        console.error('Error fetching chat:', error);
        toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Fetch active meetups with this user
    const fetchMeetups = async () => {
      try {
        const response = await axios.get(`${API}/meetups`);
        const meetups = response.data.meetups || [];
        // Find active meetup with this conversation's user
        const active = meetups.find(m => 
          (m.participant_id === otherUser?.user_id || m.creator_id === otherUser?.user_id) &&
          ["pending", "confirmed", "checked_in_one", "checked_in_both"].includes(m.status)
        );
        if (active) setActiveMeetup(active);
      } catch (error) {
        console.error('Error fetching meetups:', error);
      }
    };
    
    if (otherUser?.user_id) {
      fetchMeetups();
    }
    
    // Poll for new messages
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
        setMessages(response.data.messages || []);
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [conversationId, otherUser?.user_id]);

  // Meetup control handlers
  const handleRunningLate = async () => {
    if (!activeMeetup) return;
    try {
      await axios.put(`${API}/meetups/${activeMeetup.meetup_id}/status`, {
        status: "running_late",
        delay_minutes: runningLateMinutes,
        message: `Running ${runningLateMinutes} minutes late`
      });
      toast.success(`Notified - running ${runningLateMinutes} minutes late`);
      setShowMeetupControls(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleCantMakeIt = async () => {
    if (!activeMeetup) return;
    try {
      await axios.put(`${API}/meetups/${activeMeetup.meetup_id}/status`, {
        status: "cancelled",
        message: "Cannot make it to this meetup"
      });
      toast.success("Meetup cancelled - the other family has been notified");
      setActiveMeetup(null);
      setShowMeetupControls(false);
    } catch (error) {
      toast.error("Failed to cancel meetup");
    }
  };

  const handleNoShow = async () => {
    if (!activeMeetup) return;
    try {
      await axios.put(`${API}/meetups/${activeMeetup.meetup_id}/status`, {
        status: "no_show",
        message: "Other family did not show up"
      });
      toast.info("No-show reported");
      setActiveMeetup(null);
      setShowMeetupControls(false);
    } catch (error) {
      toast.error("Failed to report no-show");
    }
  };

  const handleCheckIn = async () => {
    if (!activeMeetup) return;
    try {
      // Try to get location if available
      let coords = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        } catch (e) {
          console.log("Location not available");
        }
      }
      
      const response = await axios.post(`${API}/meetups/${activeMeetup.meetup_id}/check-in`, {
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        location_confirmed: true
      });
      
      toast.success(response.data.message);
      if (response.data.status === "checked_in_both") {
        // Both checked in - schedule safety reminder notifications
        scheduleCheckInReminders(activeMeetup.meetup_id, otherUser?.name);
        setActiveMeetup(null);
      }
    } catch (error) {
      toast.error("Failed to check in");
    }
  };

  // Schedule push notifications for safety check-ins
  const scheduleCheckInReminders = (meetupId, familyName) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SCHEDULE_CHECKIN_REMINDER',
        meetupId,
        scheduledTime: Date.now(),
        familyName
      });
      
      // Request notification permission if not already granted
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  };

  // Cancel scheduled reminders (when meetup completes)
  const cancelCheckInReminders = (meetupId) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CANCEL_CHECKIN_REMINDERS',
        meetupId
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/conversations/${conversationId}/messages`, {
        recipient_id: otherUser?.user_id,
        content: newMessage
      });
      
      setMessages([...messages, response.data.message]);
      setNewMessage("");
      
      if (response.data.was_moderated) {
        toast.warning("Some content was filtered for safety");
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          <div>
            <p className="font-semibold">Message limit reached</p>
            <p className="text-sm">Upgrade to premium for unlimited messaging</p>
          </div>
        );
      } else {
        toast.error("Failed to send message");
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/messages" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            
            {/* User info */}
            <div className={`w-10 h-10 rounded-full flex-shrink-0 ring-2 ring-teal-100 overflow-hidden ${
              otherUser?.is_blurred ? 'bg-gradient-to-br from-sky-400 to-purple-400' : ''
            }`}>
              {!otherUser?.is_blurred && (otherUser?.photo_url || otherUser?.user_picture) ? (
                <img src={otherUser.photo_url || otherUser.user_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold">
                  {otherUser?.name?.[0] || '?'}
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-slate-800 truncate">{otherUser?.name}</h2>
                <ShieldCheck className="w-4 h-4 text-teal-500 flex-shrink-0" />
              </div>
            </div>

            {/* Plan playdate button (premium only) */}
            {user?.is_premium && (
              <Link to="/playdates">
                <Button size="sm" className="bg-teal-500 hover:bg-teal-600 text-white rounded-full" data-testid="plan-playdate-btn">
                  <Calendar className="w-4 h-4 mr-1" />
                  Plan
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Safety notice */}
          <div className="bg-sky-50 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-sky-800">
                <p className="font-medium">Keep it safe!</p>
                <p className="text-teal-600">Phone numbers and emails are automatically filtered. Use our built-in playdate planner to coordinate meetups.</p>
              </div>
            </div>
          </div>

          {/* Active Meetup Banner */}
          {activeMeetup && (
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-4 mb-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Upcoming Meetup</span>
                </div>
                <button 
                  onClick={() => setShowMeetupControls(!showMeetupControls)}
                  className="text-white/80 hover:text-white text-sm underline"
                >
                  {showMeetupControls ? "Hide options" : "Update status"}
                </button>
              </div>
              <p className="text-sm opacity-90 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                {activeMeetup.location_name}
              </p>
              <p className="text-sm opacity-90">
                <Clock className="w-4 h-4 inline mr-1" />
                {format(new Date(activeMeetup.scheduled_time), "EEE, d MMM 'at' h:mm a")}
              </p>
              
              {/* Check-in Button */}
              <Button
                onClick={handleCheckIn}
                className="w-full mt-3 bg-white text-teal-600 hover:bg-teal-50 rounded-full font-semibold"
                data-testid="check-in-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Safety Check-In (I'm Here!)
              </Button>
              
              {/* Complete Playdate Button - shows after both check in */}
              {activeMeetup.status === "checked_in_both" && (
                <Button
                  onClick={() => setShowGoodbyeModal(true)}
                  className="w-full mt-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 rounded-full font-semibold"
                  data-testid="complete-playdate-btn"
                >
                  <PartyPopper className="w-4 h-4 mr-2" />
                  Playdate Done - Time to Say Goodbye!
                </Button>
              )}
              
              {/* Meetup Controls Dropdown */}
              {showMeetupControls && (
                <div className="mt-4 pt-4 border-t border-white/20 space-y-3">
                  {/* Running Late */}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Running late?</span>
                    <select
                      value={runningLateMinutes}
                      onChange={(e) => setRunningLateMinutes(parseInt(e.target.value))}
                      className="bg-white/20 text-white rounded px-2 py-1 text-sm"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                    <Button
                      onClick={handleRunningLate}
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white rounded-full text-xs"
                    >
                      Notify
                    </Button>
                  </div>
                  
                  {/* Can't Make It */}
                  <button
                    onClick={handleCantMakeIt}
                    className="flex items-center gap-2 text-sm text-white/90 hover:text-white"
                    data-testid="cant-make-it-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Can't make it (cancel meetup)
                  </button>
                  
                  {/* No Show */}
                  <button
                    onClick={handleNoShow}
                    className="flex items-center gap-2 text-sm text-white/90 hover:text-white"
                    data-testid="no-show-btn"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Report no-show (other family didn't come)
                  </button>
                </div>
              )}
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.sender_id === user?.user_id
                  ? 'bg-teal-500 text-white rounded-br-md'
                  : 'bg-white shadow-sm rounded-bl-md'
              }`} data-testid={`message-${msg.message_id}`}>
                <p>{msg.content}</p>
                {msg.is_moderated && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${
                    msg.sender_id === user?.user_id ? 'text-sky-200' : 'text-amber-500'
                  }`}>
                    <AlertTriangle className="w-3 h-3" />
                    <span>Filtered for safety</span>
                  </div>
                )}
                <p className={`text-xs mt-1 ${
                  msg.sender_id === user?.user_id ? 'text-sky-200' : 'text-slate-400'
                }`}>
                  {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Premium notice for limited messaging */}
      {!user?.is_premium && (
        <div className="max-w-lg mx-auto px-4">
          <Link to="/subscription">
            <div className="bg-amber-50 rounded-t-2xl p-3 flex items-center gap-3 border-x border-t border-amber-200" data-testid="upgrade-notice">
              <Crown className="w-5 h-5 text-amber-500" />
              <p className="text-sm text-amber-800">Free users: 5 messages per conversation. <span className="font-medium">Upgrade for unlimited!</span></p>
            </div>
          </Link>
        </div>
      )}

      {/* Input */}
      <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-4 safe-bottom">
        <form onSubmit={handleSend} className="max-w-lg mx-auto flex gap-3">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 h-12 rounded-full bg-slate-50"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 rounded-full bg-teal-500 hover:bg-teal-600 text-white p-0"
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>

      {/* Time to Say Goodbye Modal */}
      {showGoodbyeModal && activeMeetup && (
        <TimeToSayGoodbye
          meetupId={activeMeetup.meetup_id}
          otherFamilyName={activeMeetup.is_creator ? activeMeetup.participant_name : activeMeetup.creator_name}
          onComplete={() => {
            setShowGoodbyeModal(false);
            setActiveMeetup(null);
          }}
          onClose={() => setShowGoodbyeModal(false)}
        />
      )}
    </div>
  );
};

export default Chat;